import { Popover } from 'antd';
import classnames from 'classnames';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  NormalButton,
  ConfirmModal,
  ProgressLoading,
  IcBiotricity as icBiotricity,
  IcMore as moreIcon,
} from '@/shared/ui';
import { CONFLICT_USER_COLORS, ENV, ENV_ENUM, IS_NOT_EDIT_REPORT_MESSAGE } from '@/shared/config';
import { dateToString, zeroPad, auth } from '@/shared/lib';
import './styles.scss';
import packageData from '../../../../../package.json';
import useReportHeader from './useReportHeader';

type ConflictUser = {
  firstName: string;
  lastName: string;
  token: string;
};

export type ReportHeaderProps = {
  isLoadedData: boolean;
  title: string;
  toggleDisplayStudyInfoDrawer: () => void;
  toggleDisplayPatientInfoDrawer: () => void;
  toggleDisplayFacilityInfoDrawer: () => void;
  markArtifactComponent?: React.ReactNode;
  isDisableSaveReport?: boolean;
  onClickSaveReport: () => void;
  isNotEdit?: boolean;
  profileId: string;
  studyId: string;
  isArtifactReport?: boolean;
  isStartedAiProcessing?: boolean;
  activeTab: string;
  isLoadingSaveAiReport?: boolean;
  undoStateComponent?: React.ReactNode;
  redoStateComponent?: React.ReactNode;
  isEndOfUseReport?: boolean;
  pendingProfiles?: { id: string }[];
  isGeneratingReport?: boolean;
  conflictUsers?: ConflictUser[];
};

/**
 * Renders the report page header with study identity, conflict badges and report-level actions. Shows a skeleton until isLoadedData, then the title with date of service, buttons opening the study, patient and facility drawers, badges for other users, the artifact, undo and redo slots, a more-options popover with stop-processing and, for supervisors and QA leaders, reset study behind confirm modals, and a Save button explaining any disabled state.
 *
 * @param props - Header props carrying the title, loading and edit flags, the drawer toggles, the artifact, undo and redo components, the save handler and the study and profile ids.
 * @returns The rendered report header, or its skeleton placeholder while loading.
 */
const ReportHeader = ({
  isLoadedData,
  title,
  toggleDisplayStudyInfoDrawer,
  toggleDisplayPatientInfoDrawer,
  toggleDisplayFacilityInfoDrawer,
  markArtifactComponent,
  isDisableSaveReport,
  onClickSaveReport,
  isNotEdit,
  profileId,
  studyId,
  isArtifactReport,
  isStartedAiProcessing,
  activeTab,
  isLoadingSaveAiReport,
  undoStateComponent,
  redoStateComponent,
  isEndOfUseReport,
  pendingProfiles = [],
  isGeneratingReport,
  conflictUsers,
}: ReportHeaderProps) => {
  const {
    reportInfo,
    shouldDisableResetStudyByProcessingStatus,
    isEllipsisActive,
    isOpenStopProcessingModal,
    isOpenPopover,
    isOpenResetStudyModal,
    setIsOpenResetStudyModal,
    isResetingStudy,
    isMutiUserConflict,
    handleOpenChange,
    onClickCloseTab,
    onClickResetStudy,
    handleResetStudy,
    handleStopAiProcessing,
  } = useReportHeader({
    isDisableSaveReport,
    onClickSaveReport,
    profileId,
    studyId,
    isArtifactReport,
    isStartedAiProcessing,
    activeTab,
    isLoadingSaveAiReport,
    pendingProfiles,
    conflictUsers,
  });

  const { studyFid, patientName, facilityName, reportFid, isNonPhi } = reportInfo;

  /**
   * Renders the coloured initials badge for another user working on the same report. Derives the initials from the first and last name, picks the background from CONFLICT_USER_COLORS by the doubled first character code of the first name, and always attaches a full-name tooltip.
   *
   * @param conflictUser - The other user, read for firstName, lastName and token.
   * @returns The rendered initials badge with its tooltip.
   */
  const generateConflicUserIcon = (conflictUser: ConflictUser) => {
    const { firstName, lastName, token } = conflictUser;
    const firstCharacterOfFirstName = firstName.charAt(0);
    const firstCharacterOfLastName = lastName.charAt(0);
    const charactersInHex = firstName.charCodeAt(0) + firstName.charCodeAt(0);
    const backgroundColor = CONFLICT_USER_COLORS[charactersInHex % CONFLICT_USER_COLORS.length];
    return (
      <>
        <div
          id={`conflict-user-icon-${token}`}
          className="conflict-user-icon"
          style={{ backgroundColor }}
        >
          <span className="name">{`${firstCharacterOfFirstName}${firstCharacterOfLastName}`}</span>
        </div>
        <UncontrolledTooltip
          target={`conflict-user-icon-${token}`}
          className="custom-tooltip"
          placement="bottom"
          delay={{ show: 0, hide: 0 }}
          hideArrow
        >
          <span>{`${firstName} ${lastName}`}</span>
        </UncontrolledTooltip>
      </>
    );
  };

  /**
   * Renders the report's date-of-service range in the header title. Formats the report start and stop as MM/DD/YYYY in the study timezone and joins them with an arrow.
   *
   * @returns The rendered date-of-service span.
   */
  const generateDateOfService = () => {
    const { startReport, stopReport, timezone } = reportInfo;
    const startReportDateString = dateToString(startReport, timezone, 'MM/DD/YYYY');
    const stopReportDateString = dateToString(stopReport, timezone, 'MM/DD/YYYY');
    return (
      <span className="date-of-service-container">
        <span className="date-of-service-text">{startReportDateString}</span>
        <span className="arrow-icon">&rarr;</span>
        <span className="date-of-service-text">{stopReportDateString}</span>
      </span>
    );
  };

  return isLoadedData ? (
    <div id="report-header" className="study-report-header">
      <div className="flex justify-between items-center w-100 main-container">
        <div className="flex items-center">
          <img src={icBiotricity} alt="biotricity logo" />
          <div className="block-divider" />
          <div className="block-title">
            <div className="custom-title-container">
              <span className="custom-title">{title}</span>
              {!isEndOfUseReport && (
                <>
                  <div className="custom-title-divider" />
                  {generateDateOfService()}
                </>
              )}
            </div>
            {!_.isEmpty(reportInfo) && (
              <div className="info-row">
                {!isEndOfUseReport && (
                  <>
                    <span>{`Report ${zeroPad(reportFid)}`}</span>
                    <span className="dot-divider" />
                  </>
                )}
                <NormalButton
                  id="study-id-btn"
                  isText
                  buttonName={`Study ${zeroPad(studyFid)}`}
                  onClick={toggleDisplayStudyInfoDrawer}
                />
                {!isNonPhi && (
                  <>
                    <span className="dot-divider" />
                    <NormalButton
                      id="patient-name-btn"
                      isText
                      buttonName={patientName}
                      onClick={toggleDisplayPatientInfoDrawer}
                    />
                  </>
                )}
                <span className="dot-divider" />
                <NormalButton
                  id="headerFacilityLabel"
                  isText
                  className="facility-label"
                  buttonName={facilityName}
                  onClick={toggleDisplayFacilityInfoDrawer}
                />
                {isEllipsisActive && (
                  <UncontrolledTooltip
                    className="custom-tooltip"
                    target="headerFacilityLabel"
                    placement="bottom"
                    delay={{ show: 0, hide: 0 }}
                    hideArrow
                  >
                    {facilityName}
                  </UncontrolledTooltip>
                )}
                {ENV !== ENV_ENUM.customer && (
                  <div className="ms-2">{`v${packageData.versionDisplay}.${packageData.buildNumber}`}</div>
                )}
              </div>
            )}
          </div>
        </div>

        <ConfirmModal
          isFilledRightBtn={false}
          isRedBtn={false}
          className="small-modal"
          isOpen={isOpenResetStudyModal}
          onClickLeftButton={() => {
            setIsOpenResetStudyModal(false);
          }}
          onClickRightButton={() => handleResetStudy()}
          leftButtonName={isResetingStudy ? null : 'Cancel'}
          rightButtonName="Reset"
          title="Reset study"
          isSaving={isResetingStudy}
          question="This action will reset all study changes to the state after the initial AI processing and cannot be undone. Are you sure you want to continue?"
        />
        <ConfirmModal
          isOpen={isOpenStopProcessingModal}
          onClickRightButton={onClickCloseTab}
          rightButtonName="OK"
          isRedBtn={false}
          title="Stop AI processing success"
        />

        <div className="right-component">
          <div className="avatar flex-row__ic">
            {conflictUsers?.length ? (
              <>
                {_.map(conflictUsers, (conflictUser) => generateConflicUserIcon(conflictUser))}
                <div className="vertical-line" />
              </>
            ) : null}
          </div>
          {markArtifactComponent}
          {undoStateComponent}
          {redoStateComponent}
          <Popover
            trigger="click"
            placement="bottom"
            overlayClassName="custom-ant-popover"
            open={isOpenPopover}
            onOpenChange={handleOpenChange}
            showArrow={false}
            content={
              <div className="more-options flex-col">
                <div id="stopAiProcessingBtn">
                  <button
                    id="stop-ai-processing-btn"
                    className="item-more"
                    type="button"
                    disabled={
                      isNotEdit ||
                      isLoadingSaveAiReport ||
                      isMutiUserConflict ||
                      !isStartedAiProcessing
                    }
                    onClick={handleStopAiProcessing}
                  >
                    Stop processing
                  </button>
                </div>
                {/* visible for 2 role QA Leader và Supervisor */}
                {(auth.isCallCenterSupervisor() || auth.isCallCenterQaLeader()) && (
                  <div id="resetStudyBtn">
                    <button
                      id="reset-study-btn"
                      className="item-more"
                      type="button"
                      disabled={
                        isNotEdit ||
                        isLoadingSaveAiReport ||
                        isMutiUserConflict ||
                        shouldDisableResetStudyByProcessingStatus ||
                        isGeneratingReport
                      }
                      onClick={onClickResetStudy}
                    >
                      Reset study
                    </button>
                  </div>
                )}
              </div>
            }
          >
            <IconButton
              id="more-options-btn"
              className="more-icon me-3"
              isOutline
              iconComponent={<img src={moreIcon} alt="More icon" />}
              onClick={() => {}}
            />
          </Popover>
          {isLoadingSaveAiReport ? (
            <ProgressLoading isSmall />
          ) : (
            <>
              <div id="saveReportBtn">
                <NormalButton
                  id="save-report-btn"
                  isFilled
                  buttonName="Save"
                  onClick={onClickSaveReport}
                  disabled={isDisableSaveReport || isArtifactReport || isGeneratingReport}
                />
              </div>
              <UncontrolledTooltip
                className="custom-tooltip"
                target="saveReportBtn"
                placement="bottom"
                delay={{ show: 0, hide: 0 }}
                hideArrow
              >
                {isArtifactReport
                  ? 'Artifact report due to no beats and events detected'
                  : isGeneratingReport
                    ? 'Regenerating Report'
                    : isNotEdit
                      ? IS_NOT_EDIT_REPORT_MESSAGE
                      : 'Save changes & Recalculate'}
              </UncontrolledTooltip>
            </>
          )}
        </div>
      </div>
    </div>
  ) : (
    <div className={classnames('study-report-header')}>
      <div className="flex justify-between items-center w-100 main-container">
        <div className="flex items-center">
          <img src={icBiotricity} alt="biotricity logo" />
          <div className="block-divider" />
          <div className="block-title">
            <Skeleton height={19} width={50} />
            <Skeleton height={18} width={200} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportHeader;
